import { Badge } from '@/components/shadcn/badge'
import { RestSchemas } from '@/contexts/QueryProvider'
import { ColumnDef } from '@tanstack/react-table'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { UserActionsMenu } from './UserActionsMenu'
import { OrganisationIcon } from '@/const'
import { deriveRoleNamesFromRoleNumber } from '@/util/roles'
import { ProjectName } from './ProjectName'
import { format as formatDate } from 'date-fns'
import { Avatar, AvatarBadge, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'
import clsx from 'clsx'
import { IconGlobeOutline } from '@icons/IconGlobeOutline'
import { RevealKeyButton } from './RevealKeyButton'
import { ApiAccessToggle } from './ApiAccessToggle'

type MessagesPlatformPlatformUserWithAudit = RestSchemas['Messages.Platform.PlatformUserWithAudit']

export const columns: ColumnDef<MessagesPlatformPlatformUserWithAudit>[] = [
  {
    id: 'user',
    accessorFn: (row) => `${row.givenName} ${row.familyName}`,
    header: ({ column }) => <SortingHeaderButton column={column}>User</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="group flex items-start gap-x-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full">
          {/* <IconPersonOutline className="size-5 text-muted-foreground" /> */}
          <Avatar className="size-8 overflow-visible">
            <AvatarFallback className="bg-accent text-accent-foreground">
              {getInitials(clsx(row.original.givenName, row.original.familyName))}
            </AvatarFallback>
            <AvatarBadge className={clsx('top-0', !row.original.isAPIEnabled && 'hidden')}>
              <IconGlobeOutline />
            </AvatarBadge>
          </Avatar>
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="text-base">
            {row.original.givenName} {row.original.familyName}
          </span>
          <span className="text-xs font-light leading-[1.1] text-muted-foreground">{row.original.email}</span>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) =>
      `${rowA.original.givenName} ${rowA.original.familyName}`?.localeCompare(
        `${rowB.original.givenName} ${rowB.original.familyName}`
      ),
    // size: 250,
  },
  {
    id: 'org',
    accessorKey: 'org',
    header: ({ column }) => <SortingHeaderButton column={column}>Org</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="group flex items-center gap-x-4">
        <div className="bg-offwhite flex size-10 shrink-0 items-center justify-center rounded-full">
          <OrganisationIcon className="size-5 text-muted-foreground" />
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="text-base">{row.original.organisationName}</span>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) => rowA.original.organisationName.localeCompare(rowB.original.organisationName),
    // size: 150,
  },
  {
    id: 'roles',
    header: ({ column }) => <SortingHeaderButton column={column}>Roles</SortingHeaderButton>,
    accessorKey: 'roles',
    cell: ({ cell }) => {
      const roles = deriveRoleNamesFromRoleNumber(cell.getValue() as number)
      return (
        <div className="flex flex-row flex-wrap gap-1">
          {roles.map((role) => (
            <Badge key={role} variant="secondary" className="whitespace-nowrap font-normal">
              {role}
            </Badge>
          ))}
        </div>
      )
    },
    sortingFn: (rowA, rowB) => rowA.original.roles - rowB.original.roles,
    // size: 100,
  },
  {
    id: 'approval',
    accessorKey: 'approval',
    header: ({ column }) => <SortingHeaderButton column={column}>Approval</SortingHeaderButton>,
    cell: ({ row }) => (
      <>
        {row.original.approvalDate != null && (
          <div className="flex flex-col gap-y-1">
            <span className="text-xs group-hover:underline">{formatDate(row.original.approvalDate, 'PP')}</span>
            <span className="text-xs font-light leading-[1.1] text-muted-foreground">
              {row.original.approverGivenName} {row.original.approverFamilyName}
            </span>
            <span className="text-xs font-light leading-[1.1] text-muted-foreground">{row.original.approverEmail}</span>
            <Badge variant="outline" className="whitespace-nowrap font-normal">
              <ProjectName projectId={row.original.latestApprovedProjectId} />
            </Badge>
          </div>
        )}
      </>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.approverEmail || '').localeCompare(rowB.original.approverEmail || ''),
    // size: 200,
  },
  {
    id: 'apiUser',
    header: ({ column }) => <SortingHeaderButton column={column}>API Access</SortingHeaderButton>,
    accessorKey: 'apiUser',
    cell: ({ row }) => <ApiAccessToggle userId={row.original.id} isEnabled={row.original.isAPIEnabled ?? false} />,
    sortingFn: (rowA, rowB) => (rowA.original.isAPIEnabled ? 1 : 0) - (rowB.original.isAPIEnabled ? 1 : 0),
    // size: 100,
  },
  {
    id: 'key',
    header: 'Key',
    enableSorting: false,
    cell: ({ row }) => {
      const apiKey = row.original.apiKey
      return apiKey ? <RevealKeyButton userId={row.original.id} /> : null
    },
  },
  {
    id: 'actions',
    // header: 'Actions',
    cell: ({ row }) => (
      <div className="flex justify-end pr-5">
        <UserActionsMenu user={row.original} />
      </div>
    ),
  },
]
